import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import AddBoxIcon from '@mui/icons-material/AddBox';
import QrCodeIcon from '@mui/icons-material/QrCode';
import InventoryIcon from '@mui/icons-material/Inventory';
import BarcodeIcon from '@mui/icons-material/QrCode2';
import PrintIcon from '@mui/icons-material/Print';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { signOutUser } from '../services/authService';
import { getAllProducts } from '../services/productService';
import { getAllCustomers } from '../services/customerService';

/**
 * Admin Dashboard - Pannello di controllo per gestire prodotti e QR codes
 */
function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProducts: 0,
    productsWithQR: 0,
    productsWithBarcode: 0,
    totalCustomers: 0
  });

  const loadStats = useCallback(async () => {
    try {
      const [products, customers] = await Promise.all([
        getAllProducts(),
        getAllCustomers().catch(() => [])
      ]);
      const withBarcode = products.filter(p => p.barcode).length;

      setStats({
        totalProducts: products.length,
        productsWithQR: products.length,
        productsWithBarcode: withBarcode,
        totalCustomers: customers.length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const adminActions = [
    {
      title: 'Aggiungi Nuovo Prodotto',
      description: 'Crea un nuovo prodotto scansionando il barcode o inserendo manualmente i dati',
      icon: <AddBoxIcon sx={{ fontSize: 48, color: '#4CAF50' }} />,
      action: () => navigate('/admin/add-product'),
      color: '#E8F5E9'
    },
    {
      title: 'Genera QR Code',
      description: 'Genera etichette QR brandizzate ProvaMi per i tuoi prodotti',
      icon: <QrCodeIcon sx={{ fontSize: 48, color: '#2196F3' }} />,
      action: () => navigate('/admin/qr-generator'),
      color: '#E3F2FD'
    },
    {
      title: 'Gestisci Prodotti',
      description: 'Visualizza, modifica o elimina i prodotti esistenti nel database',
      icon: <InventoryIcon sx={{ fontSize: 48, color: '#FF9800' }} />,
      action: () => navigate('/admin/manage-products'),
      color: '#FFF3E0'
    },
    {
      title: 'Clienti',
      description: 'Database contatti clienti, storico interazioni e prodotti provati',
      icon: <PeopleIcon sx={{ fontSize: 48, color: '#00897B' }} />,
      action: () => navigate('/admin/customers'),
      color: '#E0F2F1'
    },
    {
      title: 'Analytics',
      description: 'Scansioni, try-on completati, prodotti più popolari e conversioni',
      icon: <TrendingUpIcon sx={{ fontSize: 48, color: '#5C6BC0' }} />,
      action: () => navigate('/admin/analytics'),
      color: '#E8EAF6'
    },
    {
      title: 'Scansiona Barcode',
      description: 'Usa lo scanner per leggere barcode esistenti e aggiungerli rapidamente',
      icon: <BarcodeIcon sx={{ fontSize: 48, color: '#9C27B0' }} />,
      action: () => navigate('/admin/scan-barcode'),
      color: '#F3E5F5'
    },
    {
      title: 'Stampa Etichette',
      description: 'Genera e stampa etichette QR in batch per tutti i prodotti',
      icon: <PrintIcon sx={{ fontSize: 48, color: '#F44336' }} />,
      action: () => navigate('/admin/print-labels'),
      color: '#FFEBEE'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a1a1a' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            PROVAMI ADMIN
          </Typography>
          <Chip label="Dashboard" color="success" sx={{ mr: 2 }} />
          <IconButton
            color="inherit"
            onClick={handleLogout}
            title="Logout"
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Welcome Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Benvenuto nella Dashboard Admin
          </Typography>
          <Typography variant="body1">
            Gestisci i tuoi prodotti, genera QR code e monitora le scansioni dei clienti
          </Typography>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                {stats.totalProducts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Prodotti Totali
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#2196F3' }}>
                {stats.productsWithQR}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Con QR Code
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                {stats.productsWithBarcode}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Con Barcode
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#00897B' }}>
                {stats.totalCustomers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clienti
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Cards */}
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>
          Azioni Rapide
        </Typography>
        <Grid container spacing={3}>
          {adminActions.map((action, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, backgroundColor: action.color }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    {action.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom align="center" sx={{ fontWeight: 700 }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    {action.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={action.action}
                    sx={{
                      backgroundColor: '#1a1a1a',
                      '&:hover': { backgroundColor: '#333' }
                    }}
                  >
                    Apri
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Quick Tips */}
        <Paper elevation={2} sx={{ mt: 4, p: 3, backgroundColor: '#FFF9C4' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            💡 Suggerimenti Rapidi
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">
                <strong>Barcode esistenti</strong>: Ogni capo ha già un barcode EAN/UPC. Usalo per aggiungere prodotti velocemente!
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>QR brandizzati</strong>: Genera etichette ProvaMi personalizzate per un'esperienza premium
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Sistema ibrido</strong>: Supporta sia barcode che QR code per massima flessibilità
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Foto qualità</strong>: Usa foto frontali su sfondo bianco per miglior risultato try-on AI
              </Typography>
            </li>
          </ul>
        </Paper>
      </Container>
    </Box>
  );
}

export default AdminDashboard;
